import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { useQuery } from "@tanstack/react-query";
import { Toaster } from "sonner";
import axios from "axios";
import "@/App.css";
import RhythmReportPage from "@/pages/RhythmReportPage";
import RhythmReviewPage from "@/pages/RhythmReviewPage";
import RhythmFontsDemo from "@/pages/RhythmFontsDemo";
import NavigationHeader from "@/components/NavigationHeader";
import HeroSection from "@/components/HeroSection";
import MarqueeStrip from "@/components/MarqueeStrip";
import AboutSection from "@/components/AboutSection";
import EpisodesSection from "@/components/EpisodesSection";
import HostsSection from "@/components/HostsSection";
import PlatformsStrip from "@/components/PlatformsStrip";
import RhythmIntro from "@/components/RhythmIntro";
import GlowgetterWall from "@/components/GlowgetterWall";
import NewsletterSection from "@/components/NewsletterSection";
import FloatingAudioPlayer from "@/components/FloatingAudioPlayer";
import ThemeMusic from "@/components/ThemeMusic";
import Footer from "@/components/Footer";
import LowdownPage from "@/pages/LowdownPage";
import DearGlowgettersPage from "@/pages/DearGlowgettersPage";
import MorningArchivePage from "@/pages/MorningArchivePage";
import LowdownPostPage from "@/pages/LowdownPostPage";
import StudioPage from "@/pages/StudioPage";
import { EPISODES, HOSTS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-ink p-8 text-center">
          <p className="text-white/70">Something decoded itself incorrectly. Refresh to try again.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function Site({ play, nowPlaying, isPlaying }) {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = undefined;
    };
  }, []);

  const { data: episodes } = useQuery({
    queryKey: ["episodes"],
    queryFn: async () => {
      try {
        const { data } = await axios.get(`${API}/episodes`);
        return data?.length ? data : EPISODES;
      } catch {
        return EPISODES;
      }
    },
    initialData: EPISODES,
    initialDataUpdatedAt: 0,
  });

  const { data: hosts } = useQuery({
    queryKey: ["hosts"],
    queryFn: async () => {
      try {
        const { data } = await axios.get(`${API}/hosts`);
        return data?.length ? data : HOSTS;
      } catch {
        return HOSTS;
      }
    },
    initialData: HOSTS,
    initialDataUpdatedAt: 0,
  });

  return (
    <div className="relative min-h-screen overflow-x-clip bg-ink font-body text-white">
      <NavigationHeader />
      <main>
        <HeroSection latest={episodes[0]} onPlay={play} />
        <MarqueeStrip />
        <AboutSection />
        <EpisodesSection
          episodes={episodes}
          nowPlaying={nowPlaying}
          isPlaying={isPlaying}
          onPlay={play}
        />
        <HostsSection hosts={hosts} />
        <PlatformsStrip />
        <RhythmIntro />
        <GlowgetterWall />
        <NewsletterSection />
      </main>
      <Footer />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    window.__lenis?.scrollTo(0, { immediate: true });
  }, [pathname]);
  return null;
}

function AppRoot() {
  const [nowPlaying, setNowPlaying] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const stop = () => setIsPlaying(false);
    window.addEventListener("cgd:episode-ended", stop);
    return () => window.removeEventListener("cgd:episode-ended", stop);
  }, []);

  const play = (ep) => {
    if (nowPlaying?.number === ep.number) setIsPlaying((v) => !v);
    else {
      setNowPlaying(ep);
      setIsPlaying(true);
    }
  };

  return (
    <>
      <div className="grain pointer-events-none fixed inset-0 z-[60] opacity-[0.05]" />
      <Routes>
        <Route
          path="/"
          element={<Site play={play} nowPlaying={nowPlaying} isPlaying={isPlaying} />}
        />
        <Route path="/lowdown" element={<LowdownPage />} />
        <Route path="/glowgetters" element={<DearGlowgettersPage />} />
        <Route path="/morning" element={<MorningArchivePage />} />
        <Route path="/lowdown/:slug" element={<LowdownPostPage />} />
        <Route path="/rhythm" element={<RhythmReportPage />} />
        <Route path="/rhythm/:slug" element={<RhythmReviewPage />} />
        <Route path="/rhythm-fonts" element={<RhythmFontsDemo />} />
        <Route path="/studio" element={<StudioPage />} />
        <Route
          path="*"
          element={<Site play={play} nowPlaying={nowPlaying} isPlaying={isPlaying} />}
        />
      </Routes>
      <FloatingAudioPlayer
        nowPlaying={nowPlaying}
        isPlaying={isPlaying}
        onToggle={() => setIsPlaying((v) => !v)}
        onClose={() => {
          setNowPlaying(null);
          setIsPlaying(false);
        }}
      />
      <ThemeMusic lifted={!!nowPlaying} />
      <Toaster
        theme="dark"
        position="bottom-right"
        offset={88}
        toastOptions={{
          style: {
            background: "#13111A",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "#F8F8FC",
          },
        }}
      />
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <AppRoot />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
